import { useState, useEffect, useCallback, useRef } from "react";
import { generateCSSTransform } from "../utils/newPerspectiveWarp";
import { INITIAL_DOOR_DIMENSIONS } from "../Constants/constants";

export const useDragging = (points, setPoints, image) => {
  const containerRef = useRef(null);
  const [draggingHandle, setDraggingHandle] = useState(null);
  const [isDraggingDoor, setIsDraggingDoor] = useState(false);
  const [dragStartOffset, setDragStartOffset] = useState(null);
  const [showHandles, setShowHandles] = useState(true);
  const [imageTransform, setImageTransform] = useState(
    "matrix3d(1,0,0,0, 0,1,0,0, 0,0,1,0, 0,0,0,1)"
  );

  // Update transform when points change
  useEffect(() => {
    if (image && points.length === 4) {
      // Source points: corners of the original door image
      const srcPoints = [
        { x: 0, y: 0 },                                    // top-left
        { x: INITIAL_DOOR_DIMENSIONS.WIDTH, y: 0 },        // top-right
        { x: INITIAL_DOOR_DIMENSIONS.WIDTH, y: INITIAL_DOOR_DIMENSIONS.HEIGHT }, // bottom-right
        { x: 0, y: INITIAL_DOOR_DIMENSIONS.HEIGHT }        // bottom-left
      ];
      
      // Destination points: where the user has positioned the corners
      const dstPoints = [
        points[0], // top-left
        points[1], // top-right  
        points[2], // bottom-right
        points[3]  // bottom-left
      ];

      const transform = generateCSSTransform(srcPoints, dstPoints);
      setImageTransform(transform);
    }
  }, [points, image]);

  const getMousePositionInContainer = (clientX, clientY) => {
    if (!containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(clientX - rect.left, rect.width)),
      y: Math.max(0, Math.min(clientY - rect.top, rect.height)),
    };
  };

  const handleMouseDownOnHandle = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggingHandle(id);
    setIsDraggingDoor(false);
  };

  const handleMouseDownOnDoor = (e) => {
    if (draggingHandle !== null) return;
    e.preventDefault();
    
    const mousePos = getMousePositionInContainer(e.clientX, e.clientY);
    if (!mousePos || points.length !== 4) return;

    // Calculate centroid of the quadrilateral for better dragging
    const centroidX = (points[0].x + points[1].x + points[2].x + points[3].x) / 4;
    const centroidY = (points[0].y + points[1].y + points[2].y + points[3].y) / 4;

    setDragStartOffset({
      x: mousePos.x - centroidX,
      y: mousePos.y - centroidY,
    });
    setIsDraggingDoor(true);
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!containerRef.current) return;

      let clientX, clientY;
      if (e instanceof MouseEvent) {
        clientX = e.clientX;
        clientY = e.clientY;
      } else if (e.touches && e.touches[0]) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        return;
      }

      const mousePos = getMousePositionInContainer(clientX, clientY);
      if (!mousePos) return;

      if (draggingHandle !== null) {
        // Dragging individual corner
        const newPoints = [...points];
        newPoints[draggingHandle] = { x: mousePos.x, y: mousePos.y };
        setPoints(newPoints);
      } else if (isDraggingDoor && dragStartOffset && points.length === 4) {
        // Dragging entire door
        const centroidX = (points[0].x + points[1].x + points[2].x + points[3].x) / 4;
        const centroidY = (points[0].y + points[1].y + points[2].y + points[3].y) / 4;
        
        const newCentroidX = mousePos.x - dragStartOffset.x;
        const newCentroidY = mousePos.y - dragStartOffset.y;
        
        const dx = newCentroidX - centroidX;
        const dy = newCentroidY - centroidY;

        const newPoints = points.map((point) => ({
          x: Math.max(0, Math.min(point.x + dx, containerRef.current?.clientWidth || 840)),
          y: Math.max(0, Math.min(point.y + dy, containerRef.current?.clientHeight || 580)),
        }));
        setPoints(newPoints);
      }
    },
    [draggingHandle, isDraggingDoor, points, dragStartOffset, setPoints]
  );

  const handleMouseUp = useCallback(() => {
    setDraggingHandle(null);
    setIsDraggingDoor(false);
    setDragStartOffset(null);
  }, []);

  useEffect(() => {
    const currentMouseMove = (e) => handleMouseMove(e);
    const currentTouchMove = (e) => {
      e.preventDefault(); // Prevent scrolling while dragging
      handleMouseMove(e);
    };
    const currentMouseUp = () => handleMouseUp();
    const currentTouchEnd = () => handleMouseUp();

    if (draggingHandle !== null || isDraggingDoor) {
      document.addEventListener("mousemove", currentMouseMove);
      document.addEventListener("touchmove", currentTouchMove, {
        passive: false,
      });
      document.addEventListener("mouseup", currentMouseUp);
      document.addEventListener("touchend", currentTouchEnd);
    }

    return () => {
      document.removeEventListener("mousemove", currentMouseMove);
      document.removeEventListener("touchmove", currentTouchMove);
      document.removeEventListener("mouseup", currentMouseUp);
      document.removeEventListener("touchend", currentTouchEnd);
    };
  }, [draggingHandle, isDraggingDoor, handleMouseMove, handleMouseUp]);

  return {
    containerRef,
    draggingHandle,
    isDraggingDoor,
    showHandles,
    imageTransform,
    handleMouseDownOnHandle,
    handleMouseDownOnDoor,
    setShowHandles,
  };
};